/**
 * MyListsTab Component
 *
 * Tab content for "My Lists" -- shows owned lists with defaults pinned at top.
 * Includes search bar for filtering by name.
 */

import { listsQueryOptions } from '@/api/queries/lists';
import { SearchBar } from '@/components/forms/search-bar';
import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { useAppTheme } from '@/providers/theme-provider';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useQuery } from '@tanstack/react-query';
import { router } from 'expo-router';
import React, { useMemo, useState } from 'react';
import { ActivityIndicator, RefreshControl, ScrollView, View } from 'react-native';
import { ListCard } from './list-card';

interface MyListsTabProps {
  onCreateList: () => void;
}

export function MyListsTab({ onCreateList }: MyListsTabProps) {
  const theme = useAppTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebouncedValue(searchQuery, 300);

  const {
    data: listsData,
    isLoading,
    refetch,
    isRefetching,
  } = useQuery(
    listsQueryOptions({
      per_page: 50,
      scope: 'owned',
      search: debouncedSearch || undefined,
    })
  );

  const lists = listsData?.items ?? [];

  const { defaultLists, customLists } = useMemo(() => {
    const defaults = lists.filter((l) => l.type === 'default');
    const customs = lists.filter((l) => l.type === 'custom');
    return { defaultLists: defaults, customLists: customs };
  }, [lists]);

  const handleListPress = (listId: string) => {
    router.push({
      pathname: '/(root)/lists/[id]',
      params: { id: listId },
    });
  };

  return (
    <ScrollView
      className="flex-1 px-4 pt-2"
      showsVerticalScrollIndicator={false}
      refreshControl={
        <RefreshControl
          refreshing={isRefetching}
          onRefresh={() => refetch()}
          tintColor={theme.colors.primary}
        />
      }
      contentContainerStyle={{ paddingBottom: 100 }}
    >
      <SearchBar
        value={searchQuery}
        onChangeText={setSearchQuery}
        placeholder="Search my lists..."
      />

      {isLoading ? (
        <View className="items-center justify-center py-12">
          <ActivityIndicator color={theme.colors.primary} />
        </View>
      ) : lists.length === 0 ? (
        <View className="items-center justify-center py-12">
          <Ionicons name="bookmark-outline" size={48} className="text-muted-foreground" />
          <Text className="text-base font-medium mt-4 text-center text-muted-foreground">
            {debouncedSearch ? 'No matching lists' : 'No lists yet'}
          </Text>
          {!debouncedSearch && (
            <>
              <Text className="text-sm mt-2 text-center text-muted-foreground/70">
                Save menu items and restaurants to lists for easy access later.
              </Text>
              <Button
                variant="primary"
                size="sm"
                className="mt-4"
                onPress={onCreateList}
              >
                Create Your First List
              </Button>
            </>
          )}
        </View>
      ) : (
        <>
          {defaultLists.length > 0 && (
            <View className="mb-4">
              <Text className="text-sm font-semibold mb-2 text-muted-foreground uppercase tracking-wider">
                Default
              </Text>
              {defaultLists.map((list) => (
                <ListCard
                  key={list.id}
                  list={list}
                  onPress={() => handleListPress(list.id)}
                />
              ))}
            </View>
          )}

          {customLists.length > 0 && (
            <View>
              <Text className="text-sm font-semibold mb-2 text-muted-foreground uppercase tracking-wider">
                Custom
              </Text>
              {customLists.map((list) => (
                <ListCard
                  key={list.id}
                  list={list}
                  onPress={() => handleListPress(list.id)}
                />
              ))}
            </View>
          )}
        </>
      )}
    </ScrollView>
  );
}
